import { API_BASE } from './api';

// Browser side of Frolik's bot check (backend/botCheck.js). No third party,
// no cookies: the server hands out a signed challenge and the browser finds a
// number that, hashed with it, starts with enough zero bits. It runs in the
// background while the person fills in the form, so by the time they press
// the button it's usually done.

const BATCH = 512;

function leadingZeroBits(bytes) {
  let bits = 0;
  for (const byte of bytes) {
    if (byte === 0) {
      bits += 8;
      continue;
    }
    bits += Math.clz32(byte) - 24;
    break;
  }
  return bits;
}

async function solve() {
  const res = await fetch(`${API_BASE}/api/bot-check`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Bot check unavailable');
  const { challenge, difficulty } = await res.json();
  const encoder = new TextEncoder();

  for (let start = 0; ; start += BATCH) {
    const hashes = await Promise.all(
      Array.from({ length: BATCH }, (_, index) =>
        crypto.subtle.digest('SHA-256', encoder.encode(`${challenge}:${start + index}`))
      )
    );
    const found = hashes.findIndex((hash) => leadingZeroBits(new Uint8Array(hash)) >= difficulty);
    if (found !== -1) return { challenge, nonce: String(start + found) };
  }
}

// One solved check per submission: start() begins solving straight away,
// take() waits for the answer and then starts on the next one (each answer
// can only be used once).
export function createBotChecker() {
  let current = null;

  const start = () => {
    current = solve();
    current.catch(() => {}); // reported when take() is awaited
  };

  return {
    start,
    async take() {
      if (!current) start();
      const pending = current;
      current = null;
      try {
        return await pending;
      } finally {
        start();
      }
    },
  };
}

// The server says the check failed (e.g. the page was open for a long time
// and the challenge expired), so a fresh one is worth one retry.
export const isBotCheckRejection = (status, data) => status === 400 && Boolean(data && data.botCheck);
